import PropTypes from "prop-types";

// Helper to render bold, italic, inline code, and links in inline text
const renderInlineContent = (text, onBulletClick) => {
  if (!text) return null;

  // Regex matches:
  // 1. Links: \[(.*?)\]\((.*?)\)
  // 2. Bold: \*\*(.*?)\*\*
  // 3. Italics: \*(.*?)\* or _(.*?)_
  // 4. Inline code: `(.*?)`
  const regex = /(\[.*?\]\(.*?\))|(\*\*.*?\*\*)|(\*.*?\*)|(_.*?_)|(`.*?`)/g;
  const parts = String(text).split(regex);

  return parts.map((part, i) => {
    if (!part) return null;

    // Check if it's a link
    if (part.startsWith("[") && part.includes("](")) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (match) {
        return (
          <a
            key={i}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="copilot-md__link"
          >
            {match[1]}
            <i className="pi pi-external-link" style={{ fontSize: "0.65rem", marginLeft: "2px" }} />
          </a>
        );
      }
    }

    // Check if it's bold
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }

    // Check if it's italic
    if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_"))) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }

    // Check if it's inline code
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} className="copilot-md__inline-code">{part.slice(1, -1)}</code>;
    }

    return part;
  });
};

const CopilotMarkdown = ({ text, onBulletClick }) => {
  if (!text) return null;

  const nodes = [];
  const lines = String(text).split("\n");
  let currentBlockType = null; // 'code', 'table', 'list', 'num-list', null
  let blockLines = [];

  const flushBlock = (key) => {
    if (!blockLines.length) return;

    if (currentBlockType === "code") {
      const headerLine = blockLines[0].trim();
      const codeLang = headerLine.replace(/^```/, "").trim();
      const codeText = blockLines.slice(1).join("\n");
      nodes.push(
        <div key={`code-${key}`} className="copilot-md__code-block-wrapper">
          {codeLang ? <div className="copilot-md__code-lang">{codeLang}</div> : null}
          <pre className="copilot-md__code-block">
            <code>{codeText}</code>
          </pre>
        </div>
      );
    } else if (currentBlockType === "table") {
      const parseRow = (rowText) => {
        const cells = rowText.split("|").map(c => c.trim());
        if (cells[0] === "") cells.shift();
        if (cells[cells.length - 1] === "") cells.pop();
        return cells;
      };

      const headerCells = parseRow(blockLines[0]);
      const dataRows = blockLines.slice(2).map(parseRow);

      nodes.push(
        <div key={`table-${key}`} className="copilot-md__table-wrapper">
          <table className="copilot-md__table">
            <thead>
              <tr>
                {headerCells.map((cell, idx) => (
                  <th key={`th-${idx}`}>{renderInlineContent(cell, onBulletClick)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, rowIdx) => (
                <tr key={`tr-${rowIdx}`}>
                  {row.map((cell, cellIdx) => (
                    <td key={`td-${cellIdx}`}>{renderInlineContent(cell, onBulletClick)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (currentBlockType === "list") {
      nodes.push(
        <ul key={`ul-${key}`} className="copilot-md__list">
          {blockLines.map((item, idx) => (
            <li key={`li-${idx}`}>
              {onBulletClick ? (
                <button
                  type="button"
                  className="copilot-md__prompt"
                  onClick={() => onBulletClick(item)}
                >
                  {renderInlineContent(item, onBulletClick)}
                </button>
              ) : (
                renderInlineContent(item, onBulletClick)
              )}
            </li>
          ))}
        </ul>
      );
    } else if (currentBlockType === "num-list") {
      nodes.push(
        <ol key={`ol-${key}`} className="copilot-md__num-list">
          {blockLines.map((item, idx) => (
            <li key={`li-${idx}`}>
              {renderInlineContent(item, onBulletClick)}
            </li>
          ))}
        </ol>
      );
    }

    blockLines = [];
    currentBlockType = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const line = rawLine.trim();

    // 1. Code Block boundary detection
    if (line.startsWith("```")) {
      if (currentBlockType === "code") {
        flushBlock(i);
      } else {
        flushBlock(i);
        currentBlockType = "code";
        blockLines.push(rawLine);
      }
      continue;
    }

    if (currentBlockType === "code") {
      blockLines.push(rawLine);
      continue;
    }

    // 2. Table row detection
    const isTableRow = line.startsWith("|") && line.endsWith("|");
    if (isTableRow) {
      if (currentBlockType !== "table") {
        flushBlock(i);
        currentBlockType = "table";
      }
      blockLines.push(line);
      continue;
    } else if (currentBlockType === "table") {
      flushBlock(i);
    }

    // 3. Bullet list item detection
    const bulletMatch = rawLine.match(/^(\s*)[-•*]\s+(.+)$/);
    if (bulletMatch) {
      if (currentBlockType !== "list") {
        flushBlock(i);
        currentBlockType = "list";
      }
      blockLines.push(bulletMatch[2]);
      continue;
    } else if (currentBlockType === "list") {
      flushBlock(i);
    }

    // 4. Numbered list item detection
    const numListMatch = rawLine.match(/^(\s*)\d+\.\s+(.+)$/);
    if (numListMatch) {
      if (currentBlockType !== "num-list") {
        flushBlock(i);
        currentBlockType = "num-list";
      }
      blockLines.push(numListMatch[2]);
      continue;
    } else if (currentBlockType === "num-list") {
      flushBlock(i);
    }

    // 5. Empty line handling
    if (!line) {
      flushBlock(i);
      continue;
    }

    // 6. Heading detection
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      flushBlock(i);
      const level = headingMatch[1].length;
      const Tag = level === 1 ? "h1" : level === 2 ? "h2" : "h3";
      nodes.push(
        <Tag key={`h-${i}`} className={`copilot-md__h${level}`}>
          {renderInlineContent(headingMatch[2], onBulletClick)}
        </Tag>
      );
      continue;
    }

    // 7. Bold single line block (often used as section title)
    const boldOnlyMatch = line.match(/^\*\*([^*]+)\*\*$/);
    if (boldOnlyMatch) {
      flushBlock(i);
      nodes.push(
        <h3 key={`h-bold-${i}`} className="copilot-md__h3">
          {boldOnlyMatch[1]}
        </h3>
      );
      continue;
    }

    // 8. Normal paragraph line
    nodes.push(
      <p key={`p-${i}`} className="copilot-md__p">
        {renderInlineContent(line, onBulletClick)}
      </p>
    );
  }

  flushBlock("final");

  return <div className="copilot-md">{nodes}</div>;
};

CopilotMarkdown.propTypes = {
  text: PropTypes.string,
  onBulletClick: PropTypes.func,
};

export default CopilotMarkdown;
